import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { streamQuery, getSubscription, getConversationHistory, getPrice, deleteHistory } from '../api.js'
import PaywallModal from '../components/PaywallModal.jsx'
import SourceAttribution from '../components/SourceAttribution.jsx'
import { trackEvent, getUserSegment } from '../analytics.js'
import GrowLabLogo from '../components/GrowLabLogo.jsx'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../AuthContext.jsx'

export default function Chat() {
  const { t } = useTranslation()
  const { user, refreshUser } = useAuth()
  const avatarLetter = (user?.name || user?.email || '?')[0].toUpperCase()
  const [threadId, setThreadId] = useState(null)
  const [tawkUnread, setTawkUnread] = useState(false)

  useEffect(() => {
    if (user?.id) setThreadId(String(user.id))
  }, [user])

  useEffect(() => {
    if (user?.trial_tokens_remaining != null) {
      setTrialTokensRemaining(user.trial_tokens_remaining)
    }
  }, [user])

  useEffect(() => {
    try {
      Object.keys(sessionStorage)
        .filter(k => k.startsWith('sources:'))
        .forEach(k => sessionStorage.removeItem(k))
    } catch {}
  }, [])

  useEffect(() => {
    const onUnread = () => setTawkUnread(true)
    const onRead = () => setTawkUnread(false)
    window.addEventListener('growlab:tawkUnread', onUnread)
    window.addEventListener('growlab:tawkRead', onRead)
    return () => {
      window.removeEventListener('growlab:tawkUnread', onUnread)
      window.removeEventListener('growlab:tawkRead', onRead)
    }
  }, [])
  const [messages, setMessages] = useState([])
  const [question, setQuestion] = useState('')
  const [loading, setLoading] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(true)
  const [error, setError] = useState('')
  const [subChecked, setSubChecked] = useState(false)
  const [subscriptionActive, setSubscriptionActive] = useState(false)
  const [paywallOpen, setPaywallOpen] = useState(false)
  const [price, setPrice] = useState(null)
  const [rateLimited, setRateLimited] = useState(false)
  const [rateLimitResetTime, setRateLimitResetTime] = useState('')
  const [deletingHistory, setDeletingHistory] = useState(false)
  const [trialTokensRemaining, setTrialTokensRemaining] = useState(0)
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(false)
  const [resellerBlockOpen, setResellerBlockOpen] = useState(false)
  const [resellerBlockType, setResellerBlockType] = useState(null)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    async function checkSubscriptionAccess() {
      try {
        const [{ data }, priceResp] = await Promise.all([getSubscription(), getPrice()])
        setSubscriptionActive(data?.status === 'active' || data?.status === 'trialing')
        setPrice(priceResp.data)
      } catch {
        setSubscriptionActive(false)
      } finally {
        setSubChecked(true)
      }
    }
    checkSubscriptionAccess()
  }, [])

  useEffect(() => {
    if (!threadId) return
    async function hydrateHistory() {
      try {
        const { data } = await getConversationHistory(threadId)
        if (data?.messages?.length) {
          const pairs = []
          const rows = data.messages
          for (let i = 0; i < rows.length - 1; i += 2) {
            const userRow = rows[i]
            const assistantRow = rows[i + 1]
            if (userRow?.role === 'user' && assistantRow?.role === 'assistant') {
              const sources = assistantRow.sources ?? null
              pairs.push({
                id: crypto.randomUUID(),
                question: userRow.content,
                answer: assistantRow.content,
                inputTokens: assistantRow.input_tokens ?? null,
                outputTokens: assistantRow.output_tokens ?? null,
                sources,
              })
            }
          }
          setMessages(pairs)
        }
      } catch {
        // Silently start with empty messages on any error.
      } finally {
        setHistoryLoading(false)
      }
    }
    hydrateHistory()
  }, [threadId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  useEffect(() => {
    if (!loading && !('ontouchstart' in window || navigator.maxTouchPoints > 0)) {
      inputRef.current?.focus()
    }
  }, [loading])

  async function handleNewConversation() {
    setDeletingHistory(true)
    try {
      await deleteHistory(threadId)
    } catch (err) {
      console.error('Failed to delete chat history:', err)
    } finally {
      setDeletingHistory(false)
      setMessages([])
      setError('')
      setQuestion('')
      setRateLimited(false)
      setRateLimitResetTime('')
    }
  }

  async function handleSend() {
    if (!question.trim() || rateLimited) return
    if (!subscriptionActive && trialTokensRemaining === 0 && !user?.provisioned_by_reseller) {
      if (user?.is_reseller_demo) {
        setResellerBlockOpen(true)
        setResellerBlockType('trial_exhausted_reseller_demo')
      } else {
        setPaywallOpen(true)
      }
      return
    }
    setLoading(true)
    setError('')
    const currentQuestion = question
    setQuestion('')

    const msgId = crypto.randomUUID()
    setMessages((prev) => [
      ...prev,
      { id: msgId, question: currentQuestion, answer: null, answer_html: null, inputTokens: null, outputTokens: null, sources: null },
    ])

    try {
      await streamQuery(currentQuestion, threadId, {
        onSources(books) {
          setMessages((prev) =>
            prev.map((m) => m.id === msgId ? { ...m, sources: books } : m)
          )
        },
        onAnswer(data) {
          trackEvent('chat_message_sent', {
              user_segment: getUserSegment({
                provisionedByReseller: user?.provisioned_by_reseller,
                subscriptionActive,
                trialTokensRemaining,
              }),
            })
          setTrialTokensRemaining(prev =>
            Math.max(0, prev - (data.input_tokens ?? 0) - (data.output_tokens ?? 0))
          )
          refreshUser()
          setMessages((prev) =>
            prev.map((m) =>
              m.id === msgId
                ? { ...m, answer: data.answer, answer_html: data.answer_html ?? null, inputTokens: data.input_tokens, outputTokens: data.output_tokens }
                : m
            )
          )
        },
        onError(detail, status) {
          if (status === 402) {
            if (detail === 'trial_exhausted' || detail === 'trial_exhausted_reseller_demo' || detail === 'reseller_subscription_inactive') {
              setResellerBlockOpen(true)
              setResellerBlockType(detail)
              setQuestion(currentQuestion)
              if (detail === 'trial_exhausted') {
                trackEvent('trial_exhausted', { user_segment: 'expired' })
              }
            } else if (!user?.provisioned_by_reseller) {
              setPaywallOpen(true)
              setQuestion(currentQuestion)
              trackEvent('chat_quota_hit', {
                user_segment: getUserSegment({
                  provisionedByReseller: user?.provisioned_by_reseller,
                  subscriptionActive,
                  trialTokensRemaining,
                }),
              })
            }
          } else if (status === 429) {
            if (detail?.resets_at) {
              setRateLimitResetTime(new Date(detail.resets_at).toLocaleTimeString())
            }
            setRateLimited(true)
          } else {
            const msg = typeof detail === 'string' ? detail : t('chat.error.unexpected')
            setMessages((prev) => prev.filter((m) => m.id !== msgId))
            setError(msg)
            setQuestion(currentQuestion)
          }
        },
      })
    } catch {
      setMessages((prev) => prev.filter((m) => m.id !== msgId))
      setError(t('chat.error.unexpected'))
      setQuestion(currentQuestion)
    } finally {
      setLoading(false)
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  if (!subChecked) return <div className="page-loading">{t('chat.checkingSubscription')}</div>

  return (
    <div className="chat-layout">
      {paywallOpen && <PaywallModal price={price} onClose={() => setPaywallOpen(false)} source="chat_quota" />}

      {resellerBlockOpen && (
        <div className="paywall-backdrop" onClick={() => setResellerBlockOpen(false)} aria-hidden="true">
          <div
            className="paywall-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button className="paywall-close" onClick={() => setResellerBlockOpen(false)} aria-label="Close">✕</button>
            {resellerBlockType === 'trial_exhausted' ? (
              <>
                <p style={{ color: 'var(--text, #fff)', textAlign: 'center', margin: '24px 0 20px' }}>
                  {t('chat.resellerBlock.trialExhausted')}
                </p>
                {!user?.provisioned_by_reseller && (
                  <button className="paywall-subscribe-btn" onClick={() => { setResellerBlockOpen(false); setPaywallOpen(true) }}>
                    {t('chat.resellerBlock.subscribe')}
                  </button>
                )}
              </>
            ) : (
              <>
                <p style={{ color: 'var(--text, #fff)', textAlign: 'center', margin: '24px 0 20px' }}>
                  {t('chat.resellerBlock.contactProvider')}
                </p>
                <button className="paywall-subscribe-btn" onClick={() => setResellerBlockOpen(false)}>
                  {t('chat.resellerBlock.close')}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Top navbar */}
      <nav className="chat-navbar">
        <div className="navbar-logo">
          <GrowLabLogo size={22} />
          GROWLAB CLOUD
        </div>

        <div className="navbar-right">
          <button className="navbar-btn-new" onClick={handleNewConversation} disabled={deletingHistory}>
            <span className="btn-icon" aria-hidden="true">+</span>
            <span className="btn-text">{deletingHistory ? t('chat.navbar.clearing') : t('chat.navbar.newConversation')}</span>
          </button>
          {user?.role === 'reseller' && (
            <Link to="/reseller" className="navbar-support">Reseller</Link>
          )}
          {/* SUPPORT_EMAIL */}
          <a className="navbar-support" href="mailto:growlab.cloud@outlook.com">{t('chat.navbar.support')}</a>
          <Link to="/dashboard" className="navbar-avatar" title="Dashboard">
            {user?.picture
              ? <img src={user.picture} alt={avatarLetter} className="navbar-avatar-img" referrerPolicy="no-referrer" />
              : avatarLetter}
          </Link>
        </div>
      </nav>

      {/* Main area */}
      <div className="chat-main">
        {trialTokensRemaining > 0 && !trialBannerDismissed && (
          <div style={{
            background: '#78350f',
            color: '#fef3c7',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '0.85rem',
            borderBottom: '1px solid #92400e',
            flexShrink: 0,
          }}>
            <span>
              {t('chat.trialBanner', { remaining: trialTokensRemaining.toLocaleString() })}
            </span>
            <button
              onClick={() => setTrialBannerDismissed(true)}
              style={{ background: 'none', border: 'none', color: '#fef3c7', cursor: 'pointer', fontSize: '1.1rem', marginLeft: 12, padding: 0, lineHeight: 1 }}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}
        <div className="messages-area">
          <div className="messages-inner">
            {historyLoading ? (
              <div className="empty-state" style={{ fontStyle: 'italic', color: 'var(--primary)' }}>
                {t('chat.restoringConversation')}
              </div>
            ) : messages.length === 0 && !loading ? (
              <div className="empty-state">
                {t('chat.emptyState')}
              </div>
            ) : null}

            {messages.map((msg) => (
              <div key={msg.id} className="message-row">
                <div className="message-user">{msg.question}</div>
                <div className="ai-message-wrapper">
                  <GrowLabLogo size={32} />
                  <div className="ai-message-content">
                    <SourceAttribution sources={msg.sources} answer={msg.answer} answer_html={msg.answer_html} />
                  </div>
                </div>
              </div>
            ))}

            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="input-row">
          <div className="input-inner">
            <input
              ref={inputRef}
              className="chat-input"
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                rateLimited
                  ? t('chat.placeholder.rateLimited')
                  : subscriptionActive || trialTokensRemaining > 0
                  ? t('chat.placeholder.subscribed')
                  : t('chat.placeholder.noSubscription')
              }
              disabled={loading || rateLimited || historyLoading}
            />
            <button
              className="send-btn"
              onClick={handleSend}
              disabled={loading || rateLimited || !question.trim()}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>

          <div className="input-sub">
            {rateLimited && (
              <p className="rate-limit-msg">
                {t('chat.rateLimitMsg')}{rateLimitResetTime ? ` ${t('chat.rateLimitReset', { time: rateLimitResetTime })}` : ''}
              </p>
            )}
            {error && <p className="chat-error">{error}</p>}
          </div>
        </div>

        <div className="chat-footer-legal">
          <Link to="/privacy" className="chat-legal-link">{t('chat.footer.privacy')}</Link>
          <span className="chat-footer-sep">·</span>
          <Link to="/terms" className="chat-legal-link">{t('chat.footer.terms')}</Link>
          <span className="chat-footer-sep">·</span>
          <button className="chat-legal-link footer-support-btn" onClick={() => {
            if (typeof window.Tawk_API?.maximize === 'function') {
              window.Tawk_API.maximize()
              window.dispatchEvent(new CustomEvent('growlab:tawkRead'))
            } else {
              window.location.href = 'mailto:growlab.cloud@outlook.com'
            }
          }}>
            {t('chat.footer.support')}
            {tawkUnread && <span className="tawk-unread-dot" />}
          </button>
        </div>
      </div>
    </div>
  )
}
